import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'
import Typography from '@material-ui/core/Typography'
import classNames from 'classnames'

import Toggle from '~shared/components/molecules/Toggle/Toggle'
import CheckBox from '~shared/components/organisms/CheckBox'
import PasswordWithRetype from '@/shared/ui/forms/PasswordWithRetype/PasswordWithRetype'
import TextInputField from '@/shared/ui/TextInputField/TextInputField'
import DropDown from '~shared/components/molecules/DropDown'
import SmallButton from '~shared/components/molecules/SmallButton'
import { UNIT } from '~shared/assets/styles/theme'
import RatingField from '~shared/components/organisms/RatingField'
import Signature from '~shared/components/organisms/Signature'
import PageData from '~shared/utils/PageData'

import cls from './form-element.module.scss'
import { InputFile } from '~src/new/shared/ui/inputs/InputFile/InputFile'

export const FormElement = forwardRef(
  (
    {
      data,
      externalError,
      size,
      hideButtonId,
      value: propValue,
      onFieldFocus,
      inputRef,
      ...otherProps
    },
    ref,
  ) => {
    const [type, setType] = useState('')
    const [isInvalid, setIsInvalid] = useState(false)
    const [externalErrorState, setExternalErrorState] = useState(externalError)

    const elementRef = useRef(null)

    useEffect(() => {
      let newType

      if (typeof data === 'string') {
        newType = 'textLine'
      } else if (typeof data === 'function') {
        newType = 'component'
      } else {
        newType = data.type
      }

      setType(newType)
    }, [data])

    useEffect(() => {
      setExternalErrorState(externalError)
      setIsInvalid(!!externalError)
    }, [externalError])

    const value = propValue || data.value

    const handleFieldFocus = useCallback(() => {
      setIsInvalid(false)
      setExternalErrorState(undefined)
      onFieldFocus && onFieldFocus()
    }, [onFieldFocus])

    const getValue = useCallback(() => {
      return elementRef.current && elementRef.current.getValue
        ? elementRef.current.getValue()
        : undefined
    }, [])

    const checkValidity = useCallback(() => {
      if (!elementRef.current) return true

      elementRef.current.blur && elementRef.current.blur()

      if (!elementRef.current.checkValidity) {
        return true
      }

      const invalid = !elementRef.current.checkValidity()
      setIsInvalid(invalid)

      return !invalid
    }, [])

    const handleFieldBlur = useCallback(() => {
      if (!data?.validateOnBlur || !elementRef.current?.checkValidity) return
      setIsInvalid(!elementRef.current.checkValidity())
    }, [data])

    const getFiles = useCallback(() => {
      return elementRef.current && elementRef.current.getFiles ? elementRef.current.getFiles() : []
    }, [])

    useImperativeHandle(
      inputRef,
      () => ({
        getValue,
        checkValidity,
        blur: () => elementRef.current?.blur?.(),
        focus: () => elementRef.current?.focus?.(),
        getFiles,
      }),
      [getValue, checkValidity, getFiles],
    )

    const externalErrorMessage =
      typeof externalErrorState === 'string' ? externalErrorState : externalErrorState?.message

    const baseElementProps = {
      className: cls.element,
      value,
      invalid: isInvalid,
      isExternal: !!externalErrorState,
      errorText: isInvalid && externalErrorMessage,
      onFocus: handleFieldFocus,
      onBlur: handleFieldBlur,
      innerRef: ref => (elementRef.current = ref),
    }

    switch (type) {
      case 'component':
        return data()

      case 'textLine':
        return <Typography className={cls.textLine}>{data}</Typography>

      case 'label':
        return <Typography className={cls.textLine}>{data.value}</Typography>

      case 'smallButton':
        return (
          <SmallButton
            text={data.placeholder}
            href={data.to}
            onClick={data.onClick}
            icon={data.icon}
            disabled={data.disabled}
            className={classNames(cls.smallButton, data.className)}
          />
        )

      case 'spacing':
        return <div style={{ height: UNIT }} />

      case 'enum':
        return (
          <DropDown
            {...baseElementProps}
            {...otherProps}
            label={data.placeholder}
            id={data.id}
            displayEmpty={data.displayEmpty}
            enumData={data.data}
            required={data.required}
            errorText={data.errorText}
          />
        )

      case 'boolean':
        return (
          <Toggle
            {...baseElementProps}
            {...otherProps}
            label={data.placeholder}
            id={data.id}
            captions={data.data}
          />
        )
      case 'checkBox':
        return (
          <CheckBox
            {...baseElementProps}
            {...otherProps}
            id={data.id}
            text={data.placeholder}
            required={data.required}
            errorText={data.errorText}
            hideStar={data.hideStar || false}
          />
        )

      case 'passwordWithRetype':
        const { settings: { technical: { passwordPatterns } = {} } = {} } = PageData.appFrame || {}

        return (
          <PasswordWithRetype
            {...baseElementProps}
            {...otherProps}
            data={{
              ...data,
              passwordStrength: {
                patterns: passwordPatterns,
              },
            }}
          />
        )

      case 'rating':
        return <RatingField {...baseElementProps} {...otherProps} data={data} />

      case 'signature':
        return (
          <Signature
            {...baseElementProps}
            {...otherProps}
            id={data.id}
            text={data.placeholder}
            required={data.required}
            errorText={data.errorText}
            data={data}
          />
        )

      case 'date':
        return (
          <TextInputField
            {...baseElementProps}
            {...otherProps}
            data={{
              ...data,
              size,
            }}
          />
        )

      case 'file':
        return <InputFile {...baseElementProps} {...otherProps} data={data} className={cls.file} />

      default:
        return (
          <TextInputField
            {...baseElementProps}
            {...otherProps}
            data={data}
            hideButtonId={hideButtonId}
          />
        )
    }
  },
)
